/**
 * Arrived on an Order and wait PO (BLU-1506): sent -> received, cost only.
 * The box goes straight to the floor: no shelving, no stock count.
 *
 * Every line starts as arrived in full, so a complete box is one press ("All
 * arrived"). The buyer changes a number only where the box was short; only
 * those lines are sent (`short`), and the button becomes "Save what arrived".
 * A live line under the form says what Save will do to the orders, with the
 * server's own rule (per blank, oldest order first): how many move to Ready
 * to batch, how many were short and keep waiting on the next order. When the
 * PO's orders are not on hand (still loading, or more than one read holds),
 * the line counts blanks instead.
 *
 * A right-hand sheet, as in the approved mock-up. Enter walks down the
 * Arrived column and the last one hands focus to the button; Cmd/Ctrl+Enter
 * saves from anywhere in the form (the BulkStockEditorDialog convention).
 * Opened from the To buy card and the PO page's action bar, never from inside
 * another dialog. The form mounts on open, so it always starts full.
 */
import { useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCheck, Loader2 } from 'lucide-react';
import type { OrderAndWaitPo } from '@threadheads/shared';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { cn } from '@/lib/utils';
import { useArriveOrderAndWait, useOrderAndWaitPo } from '../hooks/useOrderAndWait';
import {
  blankLines,
  buildArrivedPlan,
  parseArrived,
  previewArrival,
  type ArrivedPlan,
} from '../lib/order-and-wait';
import { jitRowName } from '../lib/jit-lines';

interface ArrivedDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The sent PO; null keeps the sheet closed. */
  po: OrderAndWaitPo | null;
  /** The PO's floor (the orders read is per floor). */
  locationId: string | null;
  /** The supplier's building PO, which short blanks join; null when there is none yet. */
  nextPoNumber: string | null;
}

export function ArrivedDialog({ open, onOpenChange, po, locationId, nextPoNumber }: ArrivedDialogProps) {
  const { arrive, pendingPoId } = useArriveOrderAndWait();
  const pending = po !== null && pendingPoId === po.poId;
  return (
    <Sheet open={open && po !== null} onOpenChange={(next) => (pending ? undefined : onOpenChange(next))}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-md">
        {po && (
          <ArrivedBody
            key={po.poId}
            po={po}
            locationId={locationId}
            nextPoNumber={nextPoNumber}
            pending={pending}
            onCancel={() => onOpenChange(false)}
            onSave={async (short) => {
              const done = await arrive(
                { poId: po.poId, poNumber: po.poNumber, supplierName: po.supplierName },
                short,
              );
              if (done) onOpenChange(false);
            }}
          />
        )}
      </SheetContent>
    </Sheet>
  );
}

function ArrivedBody({
  po,
  locationId,
  nextPoNumber,
  pending,
  onCancel,
  onSave,
}: {
  po: OrderAndWaitPo;
  locationId: string | null;
  nextPoNumber: string | null;
  pending: boolean;
  onCancel: () => void;
  onSave: (short: ArrivedPlan['short']) => Promise<void>;
}) {
  const { t } = useTranslation('orders');
  const lines = useMemo(() => blankLines(po.lines), [po.lines]);
  // Raw text per blank, only for lines the buyer touched (DESIGN.md section 12.1).
  const [texts, setTexts] = useState<Record<string, string>>({});
  const inputs = useRef<Array<HTMLInputElement | null>>([]);
  const saveRef = useRef<HTMLButtonElement | null>(null);
  const plan = buildArrivedPlan(lines, texts);
  const orders = useOrderAndWaitPo(locationId, po.poId);
  const blocked = pending || plan.invalidLines > 0;
  const previewId = `oaw-arrived-preview-${po.poId}`;

  const submit = () => {
    if (blocked) return;
    void onSave(plan.short);
  };

  const onInputKeyDown = (event: KeyboardEvent<HTMLInputElement>, index: number) => {
    if (event.key !== 'Enter' || event.metaKey || event.ctrlKey) return;
    event.preventDefault();
    const next = inputs.current[index + 1];
    if (next) next.focus();
    else saveRef.current?.focus();
  };

  return (
    <div
      className="flex min-h-0 flex-1 flex-col"
      onKeyDown={(event) => {
        if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
          event.preventDefault();
          submit();
        }
      }}
    >
      <SheetHeader className="border-b px-6 py-4 pr-12 text-left">
        <SheetTitle>{t('oaw.arrived.title', { po: po.poNumber })}</SheetTitle>
        <SheetDescription>{t('oaw.arrived.body')}</SheetDescription>
      </SheetHeader>

      <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
        <table className="w-full table-fixed text-sm">
          <caption className="sr-only">{t('oaw.arrived.caption', { po: po.poNumber })}</caption>
          <thead>
            <tr className="border-b text-[11px] uppercase tracking-wide text-muted-foreground">
              <th scope="col" className="py-2 pr-2 text-left font-medium">{t('oaw.col.blank')}</th>
              <th scope="col" className="w-20 px-2 py-2 text-right font-medium">{t('oaw.col.ordered')}</th>
              <th scope="col" className="w-24 py-2 pl-2 text-right font-medium">{t('oaw.col.arrived')}</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((line, index) => {
              const name = jitRowName(line);
              const text = texts[line.skuId] ?? String(line.qty);
              const parsed = parseArrived(text, line.qty);
              const short = parsed.ok && parsed.value < line.qty;
              const errorId = `oaw-arrived-${line.skuId}-error`;
              return (
                <tr key={line.skuId} className="border-b align-top last:border-b-0">
                  <td className="py-2 pr-2">
                    <p className="break-words">{name}</p>
                    {line.sku && <p className="break-all font-mono text-[11px] text-muted-foreground">{line.sku}</p>}
                  </td>
                  <td className="px-2 py-2 text-right tabular-nums leading-9">{line.qty}</td>
                  <td className="py-2 pl-2 text-right">
                    <Input
                      ref={(el) => {
                        inputs.current[index] = el;
                      }}
                      type="text"
                      inputMode="numeric"
                      autoComplete="off"
                      value={text}
                      onChange={(event) => setTexts((current) => ({ ...current, [line.skuId]: event.target.value }))}
                      onKeyDown={(event) => onInputKeyDown(event, index)}
                      onFocus={(event) => event.target.select()}
                      disabled={pending}
                      aria-label={t('oaw.arrived.inputAria', { name })}
                      aria-invalid={!parsed.ok || undefined}
                      aria-describedby={!parsed.ok ? errorId : undefined}
                      className={cn(
                        'ml-auto h-9 w-20 text-right tabular-nums',
                        short && 'border-amber-300 bg-amber-50',
                        !parsed.ok && 'border-destructive',
                      )}
                    />
                    {!parsed.ok && (
                      <p id={errorId} className="mt-1 text-[11px] text-destructive">
                        {parsed.reason === 'tooMany'
                          ? t('oaw.arrived.tooMany', { max: line.qty })
                          : t('oaw.arrived.wholeNumber')}
                      </p>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="space-y-3 border-t px-6 py-4">
        <div id={previewId} role="status" className="rounded-md bg-muted px-3 py-2 text-sm">
          <ArrivedPreview
            poId={po.poId}
            plan={plan}
            orders={orders.loaded && !orders.hasMore ? orders.orders : null}
            nextPoNumber={nextPoNumber}
            supplierName={po.supplierName}
          />
        </div>
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:flex-wrap sm:justify-end">
          <Button type="button" variant="outline" onClick={onCancel} disabled={pending}>
            {t('oaw.dialog.back')}
          </Button>
          <Button
            ref={saveRef}
            type="button"
            onClick={submit}
            disabled={blocked}
            aria-busy={pending || undefined}
            aria-describedby={previewId}
            className="gap-1.5 bg-threadheads-purple text-white hover:bg-threadheads-purple/90"
          >
            {pending ? (
              <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden="true" />
            ) : (
              <CheckCheck className="h-4 w-4" aria-hidden="true" />
            )}
            {plan.shortLines > 0 ? t('oaw.arrived.saveShort') : t('oaw.arrived.saveAll')}
          </Button>
        </div>
      </div>
    </div>
  );
}

/** The live sentence under the form: orders when they are on hand, blanks otherwise. */
function ArrivedPreview({
  poId,
  plan,
  orders,
  nextPoNumber,
  supplierName,
}: {
  poId: string;
  plan: ArrivedPlan;
  orders: Parameters<typeof previewArrival>[0] | null;
  nextPoNumber: string | null;
  supplierName: string | null;
}) {
  const { t } = useTranslation('orders');
  const supplier = supplierName?.trim() || t('oaw.yourSupplier');
  if (plan.invalidLines > 0) {
    return <>{t('oaw.arrived.previewInvalid', { count: plan.invalidLines })}</>;
  }
  const nextOrder = nextPoNumber
    ? t('oaw.arrived.nextOrder', { po: nextPoNumber })
    : t('oaw.arrived.nextOrderUnnamed', { supplier });
  const sentences: string[] = [];
  if (orders) {
    const preview = previewArrival(orders, plan.arrivedBySku, poId);
    sentences.push(
      preview.covered > 0
        ? t('oaw.arrived.previewReleased', { count: preview.covered })
        : t('oaw.arrived.previewNoneReleased'),
    );
    if (preview.short > 0) sentences.push(t('oaw.arrived.previewShortOrders', { count: preview.short, next: nextOrder }));
    else if (plan.shortUnits === 0) sentences.push(t('oaw.arrived.previewNothingShort'));
    else sentences.push(t('oaw.arrived.previewNoOrderShort'));
  } else {
    sentences.push(t('oaw.arrived.previewUnits', { count: plan.orderedUnits, arrived: plan.arrivedUnits }));
    sentences.push(
      plan.shortUnits > 0
        ? t('oaw.arrived.previewShortUnits', { count: plan.shortUnits, next: nextOrder })
        : t('oaw.arrived.previewNothingShort'),
    );
  }
  sentences.push(t('oaw.arrived.previewNotStock'));
  return <>{sentences.join(' ')}</>;
}
